'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'
import {
  X,
  Check,
  ChevronDown,
  ArrowRight,
  Clock,
  MapPin,
  Calendar,
  Users,
  BookOpen,
  Star,
  ShieldCheck,
  Lock,
} from 'lucide-react'

// ============================================================
// DATA
// ============================================================

const PROGRAMA = [
  {
    bloco: '01',
    titulo: 'Input - Extração',
    horario: '9h - 11h',
    desc: 'Você vai aprender a extrair o que realmente importa da sua expertise. Sem achismo. Com técnica baseada em Polanyi e Klein.',
  },
  {
    bloco: '02',
    titulo: 'Gatekeeper - Posicionamento',
    horario: '11h - 12h30',
    desc: 'Quem entra e quem não entra. Defina o filtro que separa o público certo do errado e como comunicar isso.',
  },
  {
    bloco: '03',
    titulo: 'Almoço + Networking',
    horario: '12h30 - 14h',
    desc: 'Momento para trocar experiências com outros experts. Almoço incluso.',
  },
  {
    bloco: '04',
    titulo: 'Black Box - Arquitetura',
    horario: '14h - 16h30',
    desc: 'A caixa-preta que transforma input em output. Monte a estrutura interna do seu produto educacional com precisão.',
  },
  {
    bloco: '05',
    titulo: 'Output - Backward Design',
    horario: '16h30 - 18h30',
    desc: 'Defina exatamente onde seu aluno precisa chegar e desenhe o caminho de trás pra frente. Como arquiteto faz.',
  },
  {
    bloco: '06',
    titulo: 'Blueprint - Próximos Passos',
    horario: '18h30 - 21h',
    desc: 'O mapa completo. Você sai com o blueprint do seu produto educacional nas mãos. Pronto para executar.',
  },
]

const STACK_VALOR = [
  { item: 'Imersão presencial de 10 horas', valor: 'R$ 1.500' },
  { item: 'Livro APE em primeira mão', valor: 'R$ 120' },
  { item: 'Blueprint personalizado do seu produto', valor: 'R$ 800' },
  { item: 'Passaporte de Contexto (acesso digital)', valor: 'R$ 500' },
  { item: 'Almoço e coffee break', valor: 'R$ 100' },
  { item: 'Acesso ao grupo de WhatsApp pós-evento', valor: 'R$ 300' },
  { item: 'Gravação das sessões teóricas', valor: 'R$ 500' },
  { item: 'Certificado de participação', valor: 'R$ 500' },
]

const FAQ_ITEMS = [
  {
    q: 'Preciso ter um produto pronto para participar?',
    a: 'Não. O evento é justamente para quem quer estruturar o produto do zero. Você sai com o blueprint completo.',
  },
  {
    q: 'O evento é online ou presencial?',
    a: 'Presencial. São Paulo. 10 horas de imersão com mão na massa. Vagas limitadas.',
  },
  {
    q: 'Já comprei o Passaporte de Contexto. Posso ir?',
    a: 'Sim. O evento é o próximo passo natural. Você já tem a base. Agora vai construir a arquitetura completa com orientação presencial.',
  },
  {
    q: 'Posso parcelar?',
    a: 'Sim. Você pode pagar em até 12x no cartão, por Pix ou boleto bancário.',
  },
  {
    q: 'E se eu não puder ir no dia?',
    a: 'Você receberá a gravação das sessões teóricas. Mas as dinâmicas práticas são exclusivas para quem participa presencialmente.',
  },
]

const O_QUE_LEVAR = [
  'Notebook ou tablet para trabalhar no blueprint',
  'Caderno ou material para anotações',
  'Seu Passaporte de Contexto (se já tiver)',
  'Cartões de visita para networking',
  'Mente aberta e disposição para 10 horas intensas',
]

// ============================================================
// FAQ Accordion
// ============================================================

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="space-y-3">
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          className="bg-white border border-stone-200/80 rounded-2xl overflow-hidden shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full px-5 sm:px-6 py-4 flex items-center justify-between text-left cursor-pointer"
          >
            <span className="text-sm font-semibold text-stone-900 pr-4">
              {item.q}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-stone-400 shrink-0 transition-transform duration-200 ${
                openIndex === i ? 'rotate-180' : ''
              }`}
            />
          </button>
          {openIndex === i && (
            <div className="px-5 sm:px-6 pb-4">
              <p className="text-sm text-stone-500 leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function EventoContent() {
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  const totalValor = STACK_VALOR.reduce((sum, item) => {
    const n = parseInt(item.valor.replace(/\D/g, ''))
    return sum + n
  }, 0)

  return (
    <div className="min-h-screen bg-[#fafaf9] text-stone-900 font-sans antialiased">
      {/* Nav */}
      <nav className="fixed w-full z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <Link href="/">
            <Image
              src="/logo.png"
              alt="eBuz"
              width={140}
              height={40}
              className="h-8 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#programa"
              className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors"
            >
              Programa
            </a>
            <a
              href="#investimento"
              className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors"
            >
              Investimento
            </a>
            <a
              href="#faq"
              className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors"
            >
              FAQ
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Link
              href="/login"
              className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 transition-colors"
            >
              Entrar
            </Link>
            <button
              onClick={() => setShowCheckout(true)}
              className="px-5 py-2 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-lg transition-colors cursor-pointer"
            >
              Garantir Vaga
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-[#006b5f]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200/60 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">
              Evento Presencial - Vagas Limitadas
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Imersão APE -{' '}
            <span className="bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              10 horas com Jonathan Machado
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Suma por um dia inteiro. Mão na massa. Presencial.
            Você entra com expertise. Sai com o blueprint do seu produto educacional.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm text-stone-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-amber-500" /> 24 de Abril de 2026
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-amber-500" /> São Paulo - SP
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-amber-500" /> 9h às 21h
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setShowCheckout(true)}
              className="w-full sm:w-auto px-8 py-4 bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold rounded-xl shadow-lg shadow-amber-500/20 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Garantir Minha Vaga por R$ 247,90
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-stone-500">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Almoço incluso
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Livro APE de presente
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Vagas limitadas
            </span>
          </div>
        </div>
      </header>

      <main>
        {/* Programa / Agenda */}
        <section id="programa" className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                O que vai acontecer em 10 horas
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                6 blocos de trabalho intenso. Teoria aplicada na prática. Você sai com
                o produto na mão.
              </p>
            </div>

            <div className="space-y-4">
              {PROGRAMA.map((item, i) => (
                <div
                  key={i}
                  className="flex gap-4 sm:gap-6 items-start bg-stone-50 rounded-2xl border border-stone-100 p-5 sm:p-6"
                >
                  <div className="shrink-0">
                    <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/20">
                      <span className="text-sm font-black text-stone-900">
                        {item.bloco}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-1">
                      <h3 className="text-base font-bold text-stone-900">
                        {item.titulo}
                      </h3>
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        {item.horario}
                      </span>
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stack de Valor */}
        <section id="investimento" className="py-20 sm:py-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                O que está incluso
              </h2>
              <p className="text-stone-500">
                Tudo isso por um único investimento.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 shadow-lg overflow-hidden">
              <div className="divide-y divide-stone-100">
                {STACK_VALOR.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-5 sm:px-6 py-4"
                  >
                    <div className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-sm text-stone-700">{item.item}</span>
                    </div>
                    <span className="text-sm text-stone-400 line-through shrink-0 ml-4">
                      {item.valor}
                    </span>
                  </div>
                ))}
              </div>

              <div className="bg-stone-50 border-t border-stone-200 px-5 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-stone-700">
                    Valor total separado
                  </span>
                  <span className="text-sm font-bold text-stone-400 line-through">
                    R$ {totalValor.toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-8 sm:p-10 text-center text-stone-900 shadow-xl shadow-amber-500/20">
              <p className="text-sm font-bold uppercase tracking-wider mb-2 text-amber-900/70">
                Investimento do evento
              </p>
              <div className="flex items-end justify-center gap-2 mb-2">
                <span className="text-5xl sm:text-6xl font-black">R$ 247,90</span>
              </div>
              <p className="text-sm text-amber-900/60 mb-6">
                ou até 12x no cartão
              </p>

              <button
                onClick={() => setShowCheckout(true)}
                className="w-full sm:w-auto px-10 py-4 bg-stone-900 hover:bg-stone-800 text-white font-bold rounded-xl shadow-lg transition-all text-lg cursor-pointer"
              >
                Garantir Minha Vaga
                <ArrowRight className="w-5 h-5 ml-2 inline" />
              </button>

              <p className="text-xs text-amber-900/50 mt-3 flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> Pagamento seguro via Pix, cartão ou
                boleto
              </p>
            </div>
          </div>
        </section>

        {/* Quem é Jonathan */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
              <div className="shrink-0">
                <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src="/images/jonathan-machado.jpg"
                    alt="Jonathan Machado"
                    width={224}
                    height={224}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                  Jonathan Machado
                </h2>
                <p className="text-stone-600 text-lg leading-relaxed mb-4">
                  Fundador da eBuz Negócios Digitais. Criador da Metodologia APE -
                  Arquitetura de Produtos Educacionais. Há 15 anos transformando expertise
                  em produtos que funcionam.
                </p>
                <p className="text-stone-500 leading-relaxed mb-6">
                  Desenvolvedor do FunilChatbot e A1 Certificados. Já ajudou centenas de
                  experts a tirarem seus produtos do papel usando fundamentação científica
                  baseada em 12 pesquisadores e mais de 50 anos de pesquisa.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center bg-stone-50 rounded-xl p-3">
                    <div className="text-2xl font-black text-amber-500">15+</div>
                    <div className="text-xs text-stone-500 mt-1">anos de experiência</div>
                  </div>
                  <div className="text-center bg-stone-50 rounded-xl p-3">
                    <div className="text-2xl font-black text-amber-500">12</div>
                    <div className="text-xs text-stone-500 mt-1">pesquisadores na base</div>
                  </div>
                  <div className="text-center bg-stone-50 rounded-xl p-3">
                    <div className="text-2xl font-black text-amber-500">3</div>
                    <div className="text-xs text-stone-500 mt-1">empresas construídas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* O que levar */}
        <section className="py-20 sm:py-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                O que levar no dia
              </h2>
            </div>

            <div className="space-y-3">
              {O_QUE_LEVAR.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 bg-white rounded-xl border border-stone-200/80 p-4 shadow-sm"
                >
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-amber-700">{i + 1}</span>
                  </div>
                  <span className="text-sm text-stone-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 sm:py-28 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Perguntas frequentes
              </h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-20 sm:py-28 bg-stone-900 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Sua expertise merece uma arquitetura.
            </h2>
            <p className="text-stone-400 max-w-xl mx-auto mb-8">
              Não deixe o conhecimento que você acumulou em 10, 15, 20 anos morrer na sua
              cabeça. Estruture. Arquitete. Entregue.
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm text-stone-400 mb-8">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-amber-500" /> 24 de Abril de 2026
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-amber-500" /> São Paulo - SP
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-500" /> 9h às 21h
              </span>
            </div>

            <button
              onClick={() => setShowCheckout(true)}
              className="px-10 py-4 bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Garantir Minha Vaga por R$ 247,90
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>

            <div className="flex items-center justify-center gap-6 mt-6 text-sm text-stone-500">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Pagamento seguro
              </span>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* WhatsApp Floating */}
      <a
        href="https://wa.me/5561999969213"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-xl shadow-green-500/30 transition-all hover:scale-110"
        aria-label="WhatsApp"
      >
        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCheckout(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-stone-900">
                  Finalizar inscrição
                </h3>
                <p className="text-sm text-stone-500">
                  Imersão APE - R$ 247,90
                </p>
              </div>
              <button
                onClick={() => setShowCheckout(false)}
                className="text-stone-400 hover:text-stone-600 p-1 cursor-pointer"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                productType="event"
                productId="imersao-ape-2026"
                amount={24790}
                title="Imersão APE - Evento Presencial"
                description="Evento presencial 24/04/2026 - São Paulo - 10h de imersão - Livro APE incluso"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId + '&product=evento')
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
                allowBoleto={true}
                maxInstallments={12}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
